import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputWithFormat from "../components/InputWithFormat";
import { useData } from "../context/DataContext";

// import _ from "lodash";

import FloatingButtonMenu from "./layout/FloatingButtonMenu";

function Survey() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { surveys, getSurveyList, getSurveyDetail, postResult, setMessage } = useData();

  const [survey, setSurvey] = useState(null);
  const [groups, setGroups] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [requieredQuestions, setRequieredQuestions] = useState([]);
  const [respondents, setRespondents] = useState([]);

  const [group, setGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [respondentName, setRespondentName] = useState(null);
  const [anonym, setAnonym] = useState(false);
  const [surveyStep, setSurveyStep] = useState(0);
  const [result, setResult] = useState({});
  const [errors, setErrors] = useState({});
  const [location, setLocation] = useState(null);

  useEffect(() => {
    if (errors) {
      if (document.querySelector(".App-body .input-error"))
        document
          .querySelector(".App-body .input-error")
          .scrollIntoView({ behavior: "smooth" });
    }
  }, [errors]);

  useEffect(() => {
    if (survey) {
      const questionsTemp = survey.question_category.flatMap((category) =>
        category.question.flatMap((question) => {
          const questions = [question];
          if (question.children) {
            questions.push(...question.children);
          }
          return questions;
        }),
      );

      setQuestions(questionsTemp);
    }
  }, [survey]);

  useEffect(() => {
    if (group && questions) {
      setRequieredQuestions(
        questions
          .filter(
            (filterItem) =>
              filterItem.id_category === group.id &&
              filterItem.is_required &&
              filterItem.type !== "readonly",
          )
          .map((mapItem) => ({
            id: mapItem.id,
            parent_id: mapItem.parent_id,
            answer_id: Number(mapItem.parent_jawaban) || null,
          })),
      );
    }
  }, [group, questions]);

  const buildRandomId = () =>
    Date.now().toString(36) + Math.random().toString(36).substring(2);

  const fetchSurveyDetail = useCallback(() => {
    getSurveyDetail(id).then((res) => {
      setRespondents(res.data.voter);
    });
  }, [getSurveyDetail, id]);

  useEffect(() => {
    if (!surveys) {
      getSurveyList();
    } else if (surveys.length === 0) {
      navigate("/");
    } else {
      const surveyTemp = surveys.find(
        (findItem) => findItem.id.toString() === id,
      );
      if (!surveyTemp) {
        navigate("/");
        return;
      }
      const groupsTemp = (surveyTemp ?? { question_category: [] })
        .question_category;
      setSurvey(surveyTemp ?? {});
      setGroups(groupsTemp);
      fetchSurveyDetail();
    }

    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setLocation({ latitude, longitude });
          },
          (error) => {
            setMessage('Error obtaining location');
            console.error('Error obtaining location:', error);
            navigate('/');
          }
        );
      } else {
        setMessage('Geolocation is not supported');
        console.error('Geolocation is not supported by this browser.');
        navigate('/');
      }
    };

    getLocation();
  }, [fetchSurveyDetail, getSurveyList, id, navigate, setMessage, surveys]);

  useEffect(() => {
    if (
      surveyStep === 2 &&
      selectedGroup >= 0 &&
      selectedGroup < groups.length
    ) {
      setGroup(groups[selectedGroup]);
    }
  }, [groups, selectedGroup, surveyStep]);

  const renderSurveyInfo = () => (
    <div className="p-9 text-center">
      <div className="p-9">
        {respondents.length > 0 ?
          <span>Total Respondent: {respondents.length}</span>
          : null
        }
      </div>
      <Button
        onClick={() => setSurveyStep(1)}
        variant="contained"
        color="primary"
        sx={{
          padding: "10px 20px",
          fontSize: "16px",
          borderRadius: "8px",
          boxShadow: 3,
          "&:hover": {
            backgroundColor: "primary.main",
            boxShadow: 6,
          },
        }}
      >
        Start
      </Button>
    </div>
  );

  const renderInputName = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Input Name
      </Typography>

      <TextField
        id="name"
        disabled={anonym}
        label="Name"
        variant="outlined"
        fullWidth
        value={respondentName ?? ""}
        onChange={(e) => setRespondentName(e.target.value)}
        sx={{
          marginBottom: 2,
          maxWidth: 400,
        }}
        error={respondentName !== null && !respondentName}
        helperText={
          respondentName !== null && !respondentName ? "required" : ""
        }
      />
      <FormControlLabel
        control={<Checkbox checked={anonym}
          onChange={(e) => { if (e.target.checked) setRespondentName(buildRandomId); setAnonym(e.target.checked) }}
        />}
        label="Anonym"
        labelPlacement="end"
      />

      <div className="m-9 text-center">
        <Button
          onClick={() => {
            if (respondentName) setSurveyStep(2);
            else setRespondentName("");
          }}
          variant="contained"
          color="primary"
          sx={{
            padding: "10px 20px",
            fontSize: "16px",
            borderRadius: "8px",
            boxShadow: 3,
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: 6,
            },
          }}
        >
          Next
        </Button>
      </div>
    </Box>
  );

  // function getIntersection(list1, list2) {
  //   const set1 = new Set(list1);
  //   const set2 = new Set(list2);
  //   const intersection = [...set1].filter(item => set2.has(item));
  //   return intersection;
  // }

  const onChangeHandler = (id, type, val) => {
    const resultTemp = { ...result };
    resultTemp[id] = val;
    setResult({ ...resultTemp });
  };

  const onSubmitHandler = () => {
    // const resultTemp = { ...result };
    let data = questions.map((element) => {
      return {
        question_id: element["id"],
        question_type: element["type"],
        answer_id: "",
        answer: {
          type: element["type"],
          text:
            element["type"] !== "freetext" &&
              !element["format"]?.includes("scale-")
              ? ""
              : result[element["id"]]
                ? result[element["id"]][0]
                : "",
          ids:
            element["type"] === "freetext" ||
              element["format"]?.includes("scale-")
              ? []
              : result[element["id"]] || [],
        },
      };
    });

    let body = {
      name: respondentName,
      date: new Date().toLocaleDateString("en-GB"),
      lat: location?.latitude ?? '',
      long: location?.longitude ?? '',
      survey_id: survey.id,
      question: { data: data },
    };

    postResult(body).then(() => {
      resetSurvey();
    });
  };

  function resetSurvey() {
    fetchSurveyDetail();
    setSurveyStep(0);
    setResult({});
    setErrors({});
    setSelectedGroup(0);
    setAnonym(false);
    setRespondentName(null);
  }

  function getFormatType(type, format) {
    switch (type) {
      case "dropdown":
        return "single-dropdown";
      case "singlechoice":
        if (format.toString().startsWith("scale-")) {
          return `single-scale-${format.split("-")[1]}`;
        }
        return format === "img"
          ? "single-img"
          : format === "horizontal"
            ? "single-line"
            : "single-list";
      case "multiplechoice":
        return format === "img" ? "multiple-img" : "multiple-list";
      case "freetext":
        return "free-text";
      case "readonly":
        return "empty";
      default:
        return "free-text";
    }
  }

  function getData(item) {
    const output = {};
    item.forEach((element) => {
      output[element.id.toString()] = element.jawaban.toString();
    });
    return output;
  }

  const dataIsValid = () => {
    const errorsTemp = {};

    requieredQuestions.forEach((element) => {
      if (
        element.parent_id &&
        (result[element.parent_id] ?? []).includes(
          (element.answer_id ?? "").toString(),
        ) &&
        !result[element.id]
      ) {
        errorsTemp[element.id] = "required!";
      } else if (
        element.parent_id &&
        !element.answer_id &&
        !result[element.id]
      ) {
        errorsTemp[element.id] = "required!";
      } else if (!element.parent_id && !result[element.id]) {
        errorsTemp[element.id] = "required!";
      }
    });
    setErrors(errorsTemp);
    return Object.values(errorsTemp).length === 0;
  };

  const renderQuestion = (question) => {
    const answers = question.jawaban;
    const children = question.children ?? [];
    return (
      <div
        key={question.id}
        className={errors[question.id] ? "input-error" : ""}
      >
        {/* <div>
          {question.pertanyaan}
          {question.is_required ? "*" : null} ({question.type_jawaban}-
          {question.format})
        </div> */}
        {
          <InputWithFormat
            format={getFormatType(question.type_jawaban, question.format)}
            text={question.pertanyaan}
            isRequired={question.is_required}
            onChanged={(value) =>
              onChangeHandler(question.id, question.type_jawaban, value)
            }
            data={getData(answers)}
            value={result[question.id]}
            errorText={errors[question.id] ?? ""}
            label={question.label}
          />
        }
        {children.map((mapItem) => {
          const resultAnswers = result[mapItem.parent_id] ?? [];
          const visible =
            resultAnswers.includes(mapItem.parent_jawaban.toString()) ||
            question.type === "readonly" ||
            question.type === "freetext";

          return (
            <div
              style={{ display: visible ? "block" : "none" }}
              key={mapItem.id}
            >
              <InputWithFormat
                format={getFormatType(mapItem.type_jawaban, mapItem.format)}
                text={mapItem.pertanyaan}
                isRequired={mapItem.is_required}
                onChanged={(value) =>
                  onChangeHandler(mapItem.id, mapItem.type_jawaban, value)
                }
                data={getData(mapItem.jawaban)}
                value={result[mapItem.id]}
                errorText={errors[mapItem.id] ?? ""}
                label={mapItem.label}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderGroup = () => {
    if (!group) return null;
    const title = group.title;
    const question = group.question ?? [];

    return (
      <div>
        <b>{title}</b>
        {question.map((mapItem) => (
          <Box
            className="rounded-md my-5"
            key={mapItem.id}
            component="section"
            sx={{ border: "1px dashed grey" }}
          >
            {renderQuestion(mapItem)}
          </Box>
        ))}
      </div>
    );
  };

  const renderInputSurvey = () => (
    <div className="m-2 sm:m-9">
      {/* <div>{selectedGroup}</div>
      <div>{groups.length}</div>
      survey */}
      {group ? <div>{renderGroup()}</div> : null}
      {selectedGroup < groups.length - 1 ? (
        <div className="p-9 text-center ">
          <Button
            onClick={() => {
              document.querySelector(".App-body").scrollTo({
                top: 0,
                left: 0,
              });
              if (dataIsValid()) {
                setSelectedGroup((prevSelectedGroup) => prevSelectedGroup + 1);
              }
            }}
            variant="contained"
            color="primary"
            sx={{
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              boxShadow: 3,
              "&:hover": {
                backgroundColor: "primary.main",
                boxShadow: 6,
              },
            }}
          >
            Next
          </Button>
        </div>
      ) : (
        <div className="p-9 text-center">
          <Button
            onClick={() => {
              if (dataIsValid()) onSubmitHandler();
            }}
            variant="contained"
            color="primary"
            sx={{
              padding: "10px 20px",
              fontSize: "16px",
              borderRadius: "8px",
              boxShadow: 3,
              "&:hover": {
                backgroundColor: "primary.main",
                boxShadow: 6,
              },
            }}
          >
            Submit
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Container maxWidth="lg" className="h-100">
      <FloatingButtonMenu></FloatingButtonMenu>
      <Box
        sx={{
          // display: "flex",
          // flexDirection: "column",
          // alignItems: "center",
          // padding: 3,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 3,
          width: "100%",
          minHeight: "98vh",
        }}
      >
        <div className="text-3xl font-bold w-100 bg-slate-200 rounded-t-md p-5 text-center pt-14 sm:pt-6 ">
          {survey ? survey.title : ""}
        </div>
        {surveyStep === 0
          ? renderSurveyInfo()
          : surveyStep === 1
            ? renderInputName()
            : renderInputSurvey()}
      </Box>
    </Container>
  );
}

export default Survey;
